import { LEGACY_EVENT_TABLE } from './legacyImportedMessages';
// BL-177 Step 2o: "Reset test contact" - wipe one TEST phone or email back to a first-time visitor so it can be tested again.
// The database does the work and the checking (bl177_reset_test_contact: company admins only, Test list / listed test identities only,
// archives before it clears, skips anything that touches real business). These helpers only ask and describe.

export async function canResetTestContact(client, address) {
    const value = String(address || '').trim();
    if (!value || typeof client?.rpc !== 'function') return false;
    try {
        const { data, error } = await client.rpc('bl177_can_reset_test_contact', { p_address: value });
        return !error && data === true;
    } catch {
        return false;
    }
}

export async function resetTestContact(client, address, { removePortalAccess = false, conversationOnly = false } = {}) {
    const { data, error } = await client.rpc(conversationOnly ? 'bl177_reset_ai_conversation' : 'bl177_reset_test_contact', {
        p_address: String(address || '').trim(),
        ...(!conversationOnly ? { p_remove_portal_access: Boolean(removePortalAccess) } : {}),
    });
    if (error) throw new Error(error.message || 'That test contact could not be reset.');
    return data || {};
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const toClear = (counts, table) => Number(counts?.[table]?.to_clear ?? counts?.[table]?.cleared ?? 0) || 0;

// A short plain-English line (plus anything that was left alone and why) for the "done" screen and the log toast.
export function describeResetResult(result) {
    if (result?.mode === 'ai_conversation_only') {
        return {
            cleared: `Closed ${plural(Number(result.conversations_closed) || 0, 'conversation', 'conversations')} and cleared AI pauses.`,
            skipped: 'Messages, bookings, consent, first-text history, client choices and portal access were left unchanged.',
        };
    }
    const counts = result?.counts || {};
    const parts = [];
    const conversations = toClear(counts, 'intake_sessions');
    if (conversations) parts.push(plural(conversations, 'conversation', 'conversations'));
    const mirrored = toClear(counts, LEGACY_EVENT_TABLE);
    if (mirrored) parts.push(plural(mirrored, 'mirrored text', 'mirrored texts'));
    const bookings = toClear(counts, 'engagements');
    if (bookings) parts.push(plural(bookings, 'TEST booking', 'TEST bookings'));
    const portal = Number(counts?.portal_access?.removed ?? 0) || 0;
    if (portal) parts.push('client portal access');
    const skipped = [];
    for (const [table, row] of Object.entries(counts)) {
        for (const [key, value] of Object.entries(row || {})) {
            if (String(key).startsWith('skipped') && Number(value) > 0) skipped.push(`${plural(Number(value), 'row', 'rows')} in ${table.replace(/_/g, ' ')}`);
        }
    }
    return {
        cleared: parts.length ? `Cleared ${parts.join(', ')}.` : 'There was nothing to clear. The contact is already clean.',
        skipped: skipped.length ? `Left alone because they touch real business: ${skipped.join(', ')}.` : '',
    };
}
